// ── Turnover dropdown lists (Turnover Configuration, right column) ───────────
// Every list an admin can edit, with its BUILT-IN options. The database
// (TurnoverConfigOptions, db/28) stores only admin changes: a row whose
// OptionKey matches a built-in renames / reorders / retires it; any other row
// is an added option ('custom_<n>'). Nothing is seeded, so deploys never
// overwrite edits, and the lists work before anyone has touched them.
//
// Retired options disappear from new choices but a record that already chose
// one still shows it (labelFor falls back to retired rows).
// Pure: no React, no I/O.
import { SIGNAGE, CONTENT_OPPORTUNITIES } from "./marketingKickoff";
import { RESPONSIBILITY_OPTIONS } from "./contractRequirements";
import { DOC_TYPES_BY_KIND } from "./turnoverLayout";
import { REVIEW_ITEM_DEFS, reviewItemsListKey } from "./contractReviewItemDefs";

const opt = (key, label, extra = {}) => ({ key, label: label ?? key, ...extra });
const DOC_KIND_LABELS = {
  contract_review: "§2 Contract Review & Sign-Off", executed: "§3 Executed Commercial Documents",
  requirements: "§4 Contract Requirements", admin: "§4 Administrative Documents (older turnovers)",
  proposal: "Proposal / Scope of Work", raci: "RACI Matrix", estimate: "§7 Project Estimate & Budget",
  quotes: "Vendor & Subcontractor Quotes", schedule: "Project Schedule", takeoffs: "CleanSpace Takeoff Drawings",
  drawings: "Contract Drawings", specs: "Specifications", addenda: "Addenda & RFIs Affecting Pricing",
  correspondence: "Correspondence", design_cad: "Design Criteria / CAD Backgrounds",
  permits: "Permits, Bonds & AHJ (older turnovers)", risk: "Risk Register", actions: "Open Action Items",
  marketing: "§18 Marketing",
};

// storesKey: records store the option KEY (label shown via labelFor), so a
// rename relabels existing answers. Otherwise records store the label text,
// and a rename applies to new choices only.
export const OPTION_LISTS = [
  { key: "mk_signage_branding", group: "§18 Marketing", label: "Site branding signs", storesKey: true,
    builtIns: SIGNAGE.branding.items.map(i => opt(i.key, i.label)) },
  { key: "mk_signage_construction", group: "§18 Marketing", label: "Construction & safety signs", storesKey: true,
    builtIns: SIGNAGE.construction.items.map(i => opt(i.key, i.label, i.required ? { locked: "Always required" } : {})) },
  { key: "mk_signage_marketing", group: "§18 Marketing", label: "Marketing signs", storesKey: true,
    builtIns: SIGNAGE.marketing.items.map(i => opt(i.key, i.label)) },
  { key: "mk_content", group: "§18 Marketing", label: "Content opportunities", storesKey: true,
    builtIns: CONTENT_OPPORTUNITIES.map(i => opt(i.key, i.label)) },
  { key: "mk_decal_types", group: "§18 Marketing", label: "Hard hat decal types",
    help: "While this list is empty, decal type is typed freely.", builtIns: [] },
  { key: "req_responsibility", group: "Turnover sections", label: "§4 Responsible party",
    builtIns: RESPONSIBILITY_OPTIONS.map(r => opt(r, r)) },
  { key: "commission_roles", group: "Turnover sections", label: "§1 Commission split roles",
    builtIns: ["Sales", "BD", "Other"].map(r => opt(r, r)) },
  ...Object.entries(REVIEW_ITEM_DEFS).map(([dept, items]) => ({
    key: reviewItemsListKey(dept), group: "§2 Contract review items", label: dept, storesKey: true,
    help: "What this department checks, item by item, when it reviews the contract (new turnovers).",
    builtIns: items.map(i => opt(i.key, i.label)),
  })),
  ...Object.entries(DOC_TYPES_BY_KIND).map(([kind, types]) => ({
    key: `doc_types:${kind}`, group: "Document types", label: DOC_KIND_LABELS[kind] || kind,
    help: "Built-in types are locked — checks and folders depend on their exact names. Add your own below.",
    builtIns: types.map(t => opt(t, t, { locked: "Built-in" })),
  })),
];

// Shown read-only on Turnover Configuration, with why they can't be edited.
export const LOCKED_LISTS = [
  { label: "Contract review status", values: ["Pending", "Approved", "Approved with Comments", "Rejected"],
    reason: "The contract-signing gate and the database check these exact values." },
  { label: "Contract issue outcome", values: ["Issuer accepted our change", "Compromise agreed", "Withdrawn — no change needed", "Issuer declined — risk accepted as-is"],
    reason: "Stored as fixed keys the database validates." },
  { label: "Contracting entity", values: ["CleanSpace", "Allied", "CleanShield", "TECHspace"],
    reason: "Each entity sets the job number series and the SharePoint folder location." },
  { label: "Turnover status", values: ["Contract Review", "Pending Number", "Numbered", "Active", "In Progress", "Package Complete", "Turned Over"],
    reason: "The workflow moves turnovers through these automatically." },
];

const byKey = new Map(OPTION_LISTS.map(l => [l.key, l]));
export const listDef = (key) => byKey.get(key) || null;

// The merged list: built-ins (in code order) with any admin overrides, then
// added options; sorted by display order. includeRetired for the admin view.
export function effectiveOptions(listKey, rows = [], { includeRetired = false } = {}) {
  const def = byKey.get(listKey);
  if (!def) return [];
  const mine = (rows || []).filter(r => r.ListKey === listKey);
  const rowFor = new Map(mine.map(r => [r.OptionKey, r]));
  const active = (r) => !(r && (r.IsActive === false || r.IsActive === 0));
  const out = def.builtIns.map((b, i) => {
    const r = rowFor.get(b.key);
    return {
      key: b.key, label: (r && r.Label) || b.label, builtIn: true, locked: b.locked || null,
      active: b.locked === "Always required" ? true : active(r),
      order: r && r.DisplayOrder != null ? Number(r.DisplayOrder) : (i + 1) * 10,
      optionId: r ? r.OptionID : null,
    };
  });
  const builtInKeys = new Set(def.builtIns.map(b => b.key));
  for (const r of mine) {
    if (builtInKeys.has(r.OptionKey)) continue;
    out.push({ key: r.OptionKey, label: r.Label, builtIn: false, locked: null, active: active(r),
      order: Number(r.DisplayOrder ?? 1000), optionId: r.OptionID });
  }
  out.sort((a, b) => a.order - b.order || String(a.label).localeCompare(String(b.label)));
  return includeRetired ? out : out.filter(o => o.active);
}

// Label for a stored key, including a retired option's.
export function labelFor(listKey, key, rows = []) {
  return effectiveOptions(listKey, rows, { includeRetired: true }).find(o => o.key === key)?.label || key;
}

// Checkbox items for a key-storing list: active options, plus any retired one
// this record already ticked (so it can be seen and unticked).
export function checklistItems(listKey, rows = [], values = {}) {
  return effectiveOptions(listKey, rows, { includeRetired: true })
    .filter(o => o.active || (values && values[o.key]))
    .map(o => ({ key: o.key, label: o.active ? o.label : `${o.label} (retired)`, required: o.locked === "Always required" }));
}

// Select choices for a label-storing list; keeps the record's current value
// even when it was retired or renamed since.
export function selectChoices(listKey, rows = [], current = "") {
  const labels = effectiveOptions(listKey, rows).map(o => o.label);
  return current && !labels.includes(current) ? [...labels, current] : labels;
}

// Document types for an upload: built-ins, plus active added types inserted
// before "Other".
export function withExtraDocTypes(kind, baseTypes = [], rows = []) {
  const extras = effectiveOptions(`doc_types:${kind}`, rows).filter(o => !o.builtIn).map(o => o.label);
  if (!extras.length) return baseTypes;
  const i = baseTypes.indexOf("Other");
  return i < 0 ? [...baseTypes, ...extras] : [...baseTypes.slice(0, i), ...extras, ...baseTypes.slice(i)];
}

// Orders that put `options` (already in the wanted sequence) at 10, 20, 30…;
// returns only the options whose stored order has to change.
export function renumber(options) {
  return options.map((o, i) => ({ ...o, order: (i + 1) * 10 }))
    .filter((o, i) => options[i].order !== o.order);
}

// Next display order after the list's last option.
export function nextOrder(listKey, rows = []) {
  const all = effectiveOptions(listKey, rows, { includeRetired: true });
  return (all.length ? Math.max(...all.map(o => o.order)) : 0) + 10;
}

export const optionPayload = (listKey, patch, user) => {
  const p = { list_key: listKey, by: user?.name || "" };
  for (const k of ["option_key", "label", "display_order", "is_active"]) if (k in patch) p[k] = patch[k];
  if ("is_active" in p) p.is_active = p.is_active ? 1 : 0;
  return p;
};
